import { APP_BASE_HREF, registerLocaleData } from '@angular/common';
import {
  HttpErrorResponse,
  provideHttpClient,
  withInterceptors,
} from '@angular/common/http';
import {
  computed,
  DOCUMENT,
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  withComponentInputBinding,
  withRouterConfig,
} from '@angular/router';
import Bugsnag from '@bugsnag/js';
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular';
import { CodeEditorModule } from '@ngstack/code-editor';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha';
import { CookieService } from 'ngx-cookie-service';
import { MessageService } from 'primeng/api';

import { environment } from '@environments/environment';
import { FlashyCoreConfig, provideFlashyCore } from '@flashy/core';
import { FlashyUiModalHelperUri, provideFlashyUiModal } from '@flashy/ui/modal';
import { authorisationInterceptor } from '@interceptors/authorisation/authorisation.interceptor';
import { errorSuccessInterceptor } from '@interceptors/error-success/error-success.interceptor';
import { showMessageInterceptor } from '@interceptors/show-message/show-message.interceptor';
import { upgradeRequiredInterceptor } from '@interceptors/upgrade-required/upgrade-required.interceptor';

import localeEn from '@angular/common/locales/en';
import localeHe from '@angular/common/locales/he';

import { formFieldErrorsConfig, FwyFormFieldModule } from 'uix/form-field';

import { Directionality } from '@angular/cdk/bidi';
import { ACCOUNT_API_URL } from '@flashy/core/config';
import { redirectInterceptor } from '@interceptors/redirect/redirect.interceptor';
import { definePreset } from '@primeuix/themes';
import Lara from '@primeuix/themes/lara';
import { FLASHY_STORE_CONFIG_TOKEN } from '@shared/domain';
import { FlashyUiModalHelper } from '@shared/FlashyUiModalHelper';
import { AccountStore } from 'account/data-access';
import { providePrimeNG } from 'primeng/config';
import { FwyModal } from 'uix/modal';
import { AppComponent } from './app/app.component';
import routes from './app/main.routes';
import primengI18n from './primeng-i18n';

const FlashyTheme = definePreset(Lara, {
  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '#015af0',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}',
    },
  },
  components: {
    inputtext: {
      root: {
        paddingX: '6px',
        paddingY: '6px',
      },
    },
  },
});

// https://docs.bugsnag.com/platforms/javascript/customizing-error-reports/
const BugsnagClient = Bugsnag.start({
  releaseStage: environment.stage,
  apiKey: 'e4a14a648656f6f9b51c7a7b8aa39fc3',
  trackInlineScripts: false,
  generateAnonymousId: true,
  enabledReleaseStages: ['production', 'dev-production'],
  onError: ({ originalError }) => {
    const isHttpError = originalError instanceof HttpErrorResponse;
    if (isHttpError) {
      return originalError.status >= 500;
    }
    return !originalError.message?.includes('NOT REPORTING ERROR');
  },
});

// create a factory which will return the Bugsnag error handler
export function errorHandlerFactory(): BugsnagErrorHandler {
  return new BugsnagErrorHandler(BugsnagClient);
}

export function flashyInit(): () => void {
  const lang = inject(CookieService).get('lang');
  const document = inject(DOCUMENT);
  return () => {
    document.documentElement.setAttribute('lang', lang);
    document.documentElement.setAttribute('dir', lang === 'he' ? 'rtl' : 'ltr');
  };
}

if (environment.production) {
  enableProdMode();
}

registerLocaleData(localeHe, 'he');
registerLocaleData(localeEn, 'en-US');

bootstrapApplication(AppComponent, {
  providers: [
    providePrimeNG({
      theme: {
        preset: FlashyTheme,
        options: {
          darkModeSelector: false,
        },
      },
      ripple: true,
      translation: primengI18n,
    }),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
        onSameUrlNavigation: 'reload',
      })
    ),
    provideHttpClient(
      withInterceptors([
        upgradeRequiredInterceptor,
        showMessageInterceptor,
        errorSuccessInterceptor,
        authorisationInterceptor,
        redirectInterceptor,
      ])
    ),
    {
      provide: LOCALE_ID,
      useFactory: () => {
        // Replace this with your own logic for determining the locale
        const direction = inject(Directionality);
        if (direction.value.startsWith('rtl')) {
          return 'he';
        } else {
          return 'en-US';
        }
      },
    },
    importProvidersFrom(FwyFormFieldModule.forRoot(formFieldErrorsConfig())),
    provideAnimationsAsync(),
    provideFlashyCore({
      stage: environment.stage as FlashyCoreConfig['stage'],
    }),
    {
      provide: FLASHY_STORE_CONFIG_TOKEN,
      useValue: {
        root:
          environment.stage === 'production'
            ? 'https://spa.flashy.app'
            : 'https://spa.flashy.dev',
      },
    },
    provideFlashyUiModal(),
    importProvidersFrom(
      RecaptchaV3Module,
      CodeEditorModule.forRoot({
        baseUrl: 'https://cdn.flashyapp.com/spa/min',
        typingsWorkerUrl: 'assets/workers/typings-worker.js',
      }),
      CalendarModule.forRoot({
        provide: DateAdapter,
        useFactory: adapterFactory,
      }),
      GoogleTagManagerModule.forRoot({ id: environment.GTM_ID })
    ),
    MessageService,
    // {provide: ErrorHandler, useFactory: errorHandlerFactory},
    { provide: APP_BASE_HREF, useValue: '/' },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.RECAPTCHA_SITE_KEY,
    },
    provideAppInitializer(() => {
      const initializerFn = flashyInit();
      return initializerFn();
    }),
    {
      provide: ACCOUNT_API_URL,
      useFactory: ({ root }, { currentAccountId }) => {
        return computed(() => {
          return {
            root,
            account: `${root}/${currentAccountId()}`,
          };
        });
      },
      deps: [FLASHY_STORE_CONFIG_TOKEN, AccountStore],
    },
    {
      provide: FlashyUiModalHelperUri,
      useClass: FlashyUiModalHelper,
    },
    provideBrowserGlobalErrorListeners(),
    { provide: ErrorHandler, useFactory: errorHandlerFactory },
    FwyModal,
  ],
}).catch((err) => console.error(err));
